/**
 * Shared hook for persisting/rehydrating per-module simulation state.
 *
 * Wires a module hook's reducer/state into {@link simulationDraftStorage} with
 * debounced writes, a one-shot rehydrate on mount, flush-on-unload, and
 * explicit clear on successful submit/discard. Save UI feedback is opt-in via
 * `requestSaveIndicator()` so timer-driven persists stay silent. The hook
 * deliberately has a tiny surface area so each module hook keeps ownership of
 * its state shape.
 *
 * @module useSimulationDraft
 */

import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  clearDraft,
  computeTimeRemaining,
  DRAFT_SCHEMA_VERSION,
  readDraft,
  writeDraft,
  type DraftEnvelope,
  type DraftModulePayload,
  type DraftTimerAnchor,
} from '@/lib/simulationDraftStorage';
import { logger } from '@/lib/logger';
import { useExamSimulationStore } from '@/stores/useExamSimulationStore';
import { useSimulationDraftPersistStore } from '@/stores/useSimulationDraftPersistStore';
import type { CEFRLevel, ExamModule, ExamType, SelectionStep } from '@/types/exam-simulation';

/** How long to wait after the last state change before flushing to storage. */
const DEFAULT_DEBOUNCE_MS = 400;

interface UseSimulationDraftOptions<TPayload extends DraftModulePayload> {
  /** Current user id. When null, the hook is a no-op (the user is signed out). */
  readonly userId: string | null | undefined;
  /** Active simulation session id. When null, the hook is a no-op. */
  readonly sessionId: string | null | undefined;
  /** Module identifier (`reading` | `listening` | `writing` | `speaking`). */
  readonly module: ExamModule;
  /** Current selection step; persisted alongside the payload for recovery. */
  readonly step: SelectionStep;
  /** Current exam type; persisted alongside the payload for recovery summary. */
  readonly examType: ExamType | null;
  /** Current CEFR level; persisted alongside the payload for recovery summary. */
  readonly level: CEFRLevel | null;
  /** Module-owned payload to persist. Must be JSON-serializable. */
  readonly payload: TPayload;
  /** Timer anchor (initial time + `startedAt`) or null if the module has no timer. */
  readonly timer: DraftTimerAnchor | null;
  /**
   * Predicate that decides whether the current state is worth persisting.
   * Defaults to `true` so debounced writes happen on every change.
   */
  readonly shouldPersist?: (payload: TPayload) => boolean;
  /** Override the default debounce window. */
  readonly debounceMs?: number;
  /**
   * Fires exactly once on mount if a valid draft exists for the current
   * (userId, sessionId, module). Consumers typically use this to seed their
   * reducer state.
   */
  readonly onRehydrate?: (draft: DraftEnvelope<TPayload>) => void;
  /** Disable persistence/rehydrate without removing the hook from the tree. */
  readonly disabled?: boolean;
}

interface UseSimulationDraftReturn {
  /** Whether a rehydrate hand-off has already occurred for this mount. */
  readonly didRehydrate: boolean;
  /** Force any pending debounced write to flush immediately. */
  readonly flushNow: () => void;
  /** Clear the draft and cancel any pending flush. */
  readonly clear: () => void;
  /**
   * Helper for module hooks: turn a persisted anchor into a live `timeRemaining`.
   * Returns null when there is no anchor.
   */
  readonly resolveTimeRemaining: (
    anchor: DraftTimerAnchor | null | undefined,
  ) => number | null;
  /**
   * Show the local save indicator (“Saving…” / “Saved”) when the next debounced
   * write completes. Silent timer-only persists do not call this.
   */
  readonly requestSaveIndicator: () => void;
}

export function useSimulationDraft<TPayload extends DraftModulePayload>(
  options: UseSimulationDraftOptions<TPayload>,
): UseSimulationDraftReturn {
  const {
    userId,
    sessionId,
    module,
    step,
    examType,
    level,
    payload,
    timer,
    shouldPersist,
    debounceMs = DEFAULT_DEBOUNCE_MS,
    onRehydrate,
    disabled = false,
  } = options;

  const didRehydrateRef = useRef(false);
  const pendingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestEnvelopeRef = useRef<DraftEnvelope<TPayload> | null>(null);
  const indicatorRequestedRef = useRef(false);

  // Track the user's overall simulation plan so we can restore it on resume.
  const storeModules = useExamSimulationStore((s) => s.selection.modules);
  const moduleIndex = useMemo(() => {
    return Math.max(0, storeModules.indexOf(module));
  }, [storeModules, module]);

  // --- Rehydrate -------------------------------------------------------------
  useEffect(() => {
    if (disabled || didRehydrateRef.current || !userId || !sessionId) {
      return;
    }
    const draft = readDraft<TPayload>(userId, sessionId, module);
    didRehydrateRef.current = true;
    if (draft && onRehydrate) {
      try {
        onRehydrate(draft);
        logger.info('Simulation draft rehydrated', { module, sessionId });
      } catch (error) {
        logger.error('Simulation draft rehydrate threw', error, { module, sessionId });
      }
    }
    // Intentionally excluded deps: rehydrate runs once per (userId, sessionId, module).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled, userId, sessionId, module]);

  const flushLatest = useCallback(() => {
    if (pendingTimeoutRef.current) {
      clearTimeout(pendingTimeoutRef.current);
      pendingTimeoutRef.current = null;
    }
    indicatorRequestedRef.current = false;
    useSimulationDraftPersistStore.getState().reset();
    const envelope = latestEnvelopeRef.current;
    if (!envelope) return;
    writeDraft(envelope);
  }, []);

  // --- Persist (debounced) --------------------------------------------------
  useEffect(() => {
    if (disabled || !userId || !sessionId) return;
    if (shouldPersist && !shouldPersist(payload)) {
      if (indicatorRequestedRef.current) {
        indicatorRequestedRef.current = false;
        useSimulationDraftPersistStore.getState().reset();
      }
      return;
    }

    const envelope: DraftEnvelope<TPayload> = {
      v: DRAFT_SCHEMA_VERSION,
      userId,
      sessionId,
      module,
      step,
      examType,
      level,
      modules: storeModules,
      moduleIndex,
      timer: timer ?? null,
      updatedAt: Date.now(),
      payload,
    };
    latestEnvelopeRef.current = envelope;

    if (pendingTimeoutRef.current) {
      clearTimeout(pendingTimeoutRef.current);
    }
    pendingTimeoutRef.current = setTimeout(() => {
      pendingTimeoutRef.current = null;
      const ok = writeDraft(envelope);
      if (indicatorRequestedRef.current) {
        indicatorRequestedRef.current = false;
        useSimulationDraftPersistStore.getState().notifyWriteFinished(ok);
      }
    }, debounceMs);

    return () => {
      if (pendingTimeoutRef.current) {
        clearTimeout(pendingTimeoutRef.current);
        pendingTimeoutRef.current = null;
      }
    };
  }, [disabled, userId, sessionId, module, step, examType, level, storeModules, moduleIndex, timer, payload, debounceMs, shouldPersist]);

  // --- Flush on unload ------------------------------------------------------
  useEffect(() => {
    if (disabled || !userId || !sessionId) return;
    const handleBeforeUnload = () => flushLatest();
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') flushLatest();
    };
    globalThis.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      globalThis.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [disabled, userId, sessionId, flushLatest]);

  // --- Final flush on unmount -----------------------------------------------
  useEffect(() => {
    return () => {
      flushLatest();
    };
  }, [flushLatest]);

  const flushNow = useCallback(() => {
    flushLatest();
  }, [flushLatest]);

  const clear = useCallback(() => {
    if (pendingTimeoutRef.current) {
      clearTimeout(pendingTimeoutRef.current);
      pendingTimeoutRef.current = null;
    }
    indicatorRequestedRef.current = false;
    useSimulationDraftPersistStore.getState().reset();
    latestEnvelopeRef.current = null;
    if (userId && sessionId) {
      clearDraft(userId, sessionId, module);
    }
  }, [userId, sessionId, module]);

  const resolveTimeRemaining = useCallback(
    (anchor: DraftTimerAnchor | null | undefined): number | null => {
      if (!anchor) return null;
      return computeTimeRemaining(anchor);
    },
    [],
  );

  const requestSaveIndicator = useCallback(() => {
    indicatorRequestedRef.current = true;
    useSimulationDraftPersistStore.getState().scheduleDebouncedWrite();
  }, []);

  return useMemo(
    () => ({
      didRehydrate: didRehydrateRef.current,
      flushNow,
      clear,
      resolveTimeRemaining,
      requestSaveIndicator,
    }),
    [flushNow, clear, resolveTimeRemaining, requestSaveIndicator],
  );
}
